import { Box, Typography } from '@mui/material'
import { ChartDataPoint, ChartSeries, QueryMode } from '../types'
import { getScoreColor } from '../../../../../../../utils/methods/chartColors'
import { Color } from '../../../../../../../utils/theme/types/color'

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{ value: number; name: string; color: string }>
  queryMode: QueryMode
  data: ChartDataPoint[]
  series: ChartSeries[]
  totalResultCount: number
  selectedResultIds: string[]
  t: (key: string, options?: Record<string, unknown>) => string
  themeColors: Color
}

const CustomTooltip = ({
  active,
  payload,
  queryMode,
  data,
  series,
  selectedResultIds,
  t,
  themeColors,
}: CustomTooltipProps) => {
  if (!active || !payload) return null

  const relevant = payload.filter((p) => p.value !== null && p.value !== undefined)
  if (relevant.length === 0) return null

  const selectedSeries = series.filter((s) => selectedResultIds.includes(s.id))
  let totalSum = 0
  let totalCount = 0
  for (const s of selectedSeries) {
    for (const point of data) {
      const val = point[s.id]
      if (val !== null && val !== undefined) {
        totalSum += val as number
        totalCount++
      }
    }
  }
  const globalAvg = totalCount > 0 ? Math.round(totalSum / totalCount) : 0

  const singleSelected = selectedResultIds.length === 1

  let showHeader = false
  let headerText = ''
  let headerScore = 0

  if (queryMode === 'one-unit-many-questionnaires') {
    showHeader = false
  } else if (queryMode === 'one-unit-one-questionnaire') {
    showHeader = true
    headerText = t('dashboard.storePerformance.chart.periodPerformance')
    headerScore = globalAvg
  }

  const showItemList = !(showHeader && singleSelected)

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '12px',
        fontSize: '13px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: '220px',
        maxWidth: '700px',
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            mb: showItemList ? '8px' : '0px',
            pb: showItemList ? '8px' : '0px',
          }}
        >
          <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '14px' }}>
            {headerText}
          </Typography>
          <Box
            sx={{
              backgroundColor: getScoreColor(headerScore, themeColors),
              color: '#fff',
              fontSize: '11px',
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: '4px',
              minWidth: '36px',
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {headerScore}%
          </Box>
        </Box>
      )}

      {showItemList && (
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '12px',
        }}>
          {relevant.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <Box sx={{ width: '10px', height: '10px', backgroundColor: entry.color, borderRadius: '2px', flexShrink: 0, mt: '3px' }} />
              <Box sx={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}>
                <Typography sx={{ fontSize: '13px', lineHeight: '16px', color: '#374151' }}>
                  {entry.name}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: getScoreColor(entry.value, themeColors),
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  minWidth: '36px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                {entry.value}%
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

interface CustomPieTooltipProps {
  active?: boolean
  payload?: Array<{ payload: { _metaScore: number } }>
  t: (key: string, options?: Record<string, unknown>) => string
  themeColors: Color
}

const CustomPieTooltip = ({ active, payload, t, themeColors }: CustomPieTooltipProps) => {
  if (!active || !payload || payload.length === 0) return null

  const metaScore = payload[0]?.payload?._metaScore

  if (metaScore == null) return null

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '10px 14px',
        fontSize: '13px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: '180px',
      }}
    >
      <Typography sx={{ fontWeight: 600, color: '#1f2937', fontSize: '14px', flex: 1 }}>
        {t('dashboard.storePerformance.chart.globalAverage')}
      </Typography>
      <Box
        sx={{
          backgroundColor: getScoreColor(metaScore, themeColors),
          color: '#fff',
          fontSize: '12px',
          fontWeight: 600,
          padding: '2px 10px',
          borderRadius: '4px',
          minWidth: '40px',
          textAlign: 'center',
          flexShrink: 0,
        }}
      >
        {metaScore}%
      </Box>
    </Box>
  )
}

export { CustomTooltip, CustomPieTooltip }
